import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CloudArrowUpIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { getSubjects } from "../../Requests/SubjectRequests";
import Loader from "../Loader";

export default function IdentifyUsersModal({ handleClose, speakerNames, handleSaveNames, analysis }) {
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [currentNames, setCurrentNames] = useState(
        typeof speakerNames === 'string' ? JSON.parse(speakerNames) : speakerNames
    );
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [speakerSubjects, setSpeakerSubjects] = useState(
        analysis?.speaker_subjects ? JSON.parse(analysis.speaker_subjects) : {}
    );

    useEffect(() => {
        if (analysis?.project) {
            getSubjects({ project_id: analysis.project.id })
                .then((response) => {
                    setSubjects(response.data.data);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [analysis]);

    const closeModal = () => {
        handleClose();
        setOpen(false);
    };

    const handleInputChange = (key, newValue) => {
        setCurrentNames({
            ...currentNames,
            [key]: newValue,
        });
    };

    const handleSubjectChange = (key, subjectUuid) => {
        setSpeakerSubjects({
            ...speakerSubjects,
            [key]: subjectUuid,
        });
    };

    const handleSave = () => {
        setSaving(true);

        const mappedData = {};
        Object.keys(currentNames).forEach(key => {
            mappedData[key] = {
                name: currentNames[key],
                subject_uuid: speakerSubjects[key] || null
            };
        });

        handleSaveNames({
            speaker_names: mappedData,
            analysis_uuid: analysis.uuid
        });
    };

    if (loading) {
        return (
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={closeModal}>
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="bg-white rounded-lg p-6">
                            <Loader className="w-8 h-8" />
                        </Dialog.Panel>
                    </div>
                </Dialog>
            </Transition.Root>
        );
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal}>
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg w-full max-w-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <Dialog.Title className="text-lg font-medium">
                                    {t('UpdateNamesModal.Title')}
                                </Dialog.Title>
                                <div className="text-md font-medium">
                                    {t('UpdateNamesModal.Description')}
                                </div>
                            </div>

                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {Object.entries(currentNames).map(([key, value]) => {
                                const selectedSubjectUuid = speakerSubjects[key];
                                const selectedSubject = subjects.find(s => s.uuid === selectedSubjectUuid);

                                return (
                                    <div key={key} className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <UserIcon className="h-5 w-5 text-gray-400" />
                                            <span className="font-medium">{key}</span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                                    placeholder={t('UpdateNamesModal.NamePlaceholder')}
                                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                                />
                                            </div>

                                            <select
                                                value={selectedSubjectUuid || ''}
                                                onChange={(e) => handleSubjectChange(key, e.target.value)}
                                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                            >
                                                <option value="">{t('UpdateNamesModal.SelectSubject')}</option>
                                                {subjects.map((subject) => (
                                                    <option
                                                        key={subject.uuid}
                                                        value={subject.uuid}
                                                    >
                                                        {subject.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end mt-6 pt-4 border-t">
                            <button
                                className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                                disabled={saving}
                                onClick={handleSave}
                            >
                                {saving ? (
                                    <Loader className="w-4 h-4" />
                                ) : (
                                    <>
                                        <CloudArrowUpIcon className="h-4 w-4" />
                                        {t('UpdateNamesModal.SaveButton')}
                                    </>
                                )}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
